import { LengthOfHtmlTextString, truncateHtml } from "helpers/stringHelper";
import SkeletonBase from "newui/components/Skeleton";
import TextBoxHTML from "newui/components/TextBoxHTML";
import { ReactNode, useMemo } from "react";
import { cn } from "utils/helpers";

interface ClickExpandTextModalProps {
	isLoading: boolean;
	maxTextDisplay?: number;
	text: string | null;
	textClassName?: string;
	title: string | null;
	titleClassName?: string;
	setShowModal: (show: boolean) => void;
}

export const ClickExpandTextModal = ({ isLoading, maxTextDisplay = 150, text, textClassName, title, titleClassName, setShowModal }: ClickExpandTextModalProps) => {
	const length = LengthOfHtmlTextString(text);

	const hasText = length > 0;

	const truncatedHtml = useMemo(() => {
		if (!text) {
			return null;
		}
		return truncateHtml(text, { maxCharacters: maxTextDisplay, maxLines: 3 });
	}, [text, maxTextDisplay]);

	const showModal = () => {
		setShowModal(true);
	}

	if (!hasText) {
		return null;
	}

	if (isLoading) {
		return (
			<SkeletonBase
				width="100%"
				height="48px"
			/>
		);
	}

	const content = (
		title: string,
		displayValue: string,
		showMore: boolean
	): ReactNode => {
		return (
			<div className="flex flex-col gap-1">
				<p className={cn("text-left font-roboto font-bold text-gray-text-400", titleClassName)}>
					{title}
				</p>
				<TextBoxHTML
					html={displayValue}
					className={cn("text-gray-text-600 text-base font-normal m-0", textClassName)}
				/>
				{showMore &&
					<div className="text-blue-600 underline mt-1 decoration-blue-300 underline-offset-2 transition-colors hover:text-blue-800 hover:decoration-blue-600 cursor-pointer text-sm" onClick={showModal}>
						<i>click to see more...</i>
					</div>
				}
			</div>
		);
	};

	if (!truncatedHtml.wasTruncated) {
		return <div className="my-2">{content(title, text ?? "", false)}</div>;
	}

	return (
		hasText && (
			content(title ?? "", truncatedHtml?.value ?? "", true)
		)
	);
};
